
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AgentModel } from "@/components/candidates/types/agent";
import { toast } from "sonner";

export const useAgentModels = (includeDeprecated = false) => {
  return useQuery({
    queryKey: ['agent-models', includeDeprecated],
    queryFn: async () => {
      let query = supabase
        .from('agent_models')
        .select('*')
        .eq('is_available', true);
      
      if (!includeDeprecated) {
        query = query.eq('is_deprecated', false);
      }
      
      const { data, error } = await query.order('provider', { ascending: true });

      if (error) {
        console.error('Error fetching agent models:', error);
        throw error;
      }

      return data?.map(model => ({
        id: model.id,
        name: model.name,
        provider: model.provider as any,
        description: model.description || '',
        capabilities: model.capabilities || [],
        latency: model.latency as any,
        costTier: model.cost_tier as any,
        optimizedFor: model.optimized_for || [],
        contextWindow: model.context_window,
        maxOutputTokens: model.max_output_tokens,
        inputCostPer1k: model.input_cost_per_1k,
        outputCostPer1k: model.output_cost_per_1k,
        lastSyncedAt: model.last_synced_at,
        syncSource: model.sync_source,
        isDeprecated: model.is_deprecated,
        isAvailable: model.is_available,
        providerModelId: model.provider_model_id,
        version: model.version,
        modelFamily: model.model_family,
        performanceMetrics: model.performance_metrics,
        featureFlags: model.feature_flags,
        apiEndpoint: model.api_endpoint,
        availabilityRegions: model.availability_regions,
        deprecationDate: model.deprecation_date,
      })) as AgentModel[] || [];
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    refetchInterval: 10 * 60 * 1000, // Auto-refetch every 10 minutes
  });
};

export const useBestAvailableModel = (
  capabilities: string[] = [],
  costPreference: 'low' | 'medium' | 'high' = 'medium',
  latencyPreference: 'low' | 'medium' | 'high' = 'medium'
) => {
  return useQuery({
    queryKey: ['best-available-model', capabilities, costPreference, latencyPreference],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_best_available_model', {
        p_capabilities: capabilities,
        p_cost_preference: costPreference,
        p_latency_preference: latencyPreference,
      });

      if (error) {
        throw error;
      }

      return data?.[0] || null;
    },
    enabled: capabilities.length > 0,
  });
};

export const useCreateAgentModel = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (model: Omit<AgentModel, 'id'> & { id?: string }) => {
      const { data, error } = await supabase
        .from('agent_models')
        .insert({
          id: model.id || crypto.randomUUID(),
          name: model.name,
          provider: model.provider,
          description: model.description,
          capabilities: model.capabilities,
          latency: model.latency,
          cost_tier: model.costTier,
          optimized_for: model.optimizedFor,
          context_window: model.contextWindow,
          max_output_tokens: model.maxOutputTokens,
          input_cost_per_1k: model.inputCostPer1k,
          output_cost_per_1k: model.outputCostPer1k,
          is_available: model.isAvailable ?? true,
          is_deprecated: model.isDeprecated ?? false,
          sync_source: model.syncSource || 'manual',
          provider_model_id: model.providerModelId,
          version: model.version,
          model_family: model.modelFamily,
          performance_metrics: model.performanceMetrics,
          feature_flags: model.featureFlags,
          api_endpoint: model.apiEndpoint,
          availability_regions: model.availabilityRegions,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-models'] });
      toast.success('Agent model created successfully');
    },
    onError: (error) => {
      console.error('Error creating agent model:', error);
      toast.error('Failed to create agent model');
    }
  });
};
